import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import IconGoBack from '../../components/icons/IconGoBack';
import IconEdit from '../../components/icons/IconEdit';
import usePersistedState from '../../hooks/usePersistedState';
import useCourseWorkspace from './useCourseWorkspace';
import OutlineTree from './OutlineTree';
import DetailPanel from './DetailPanel';
import CourseEditForm from './CourseEditForm';
import TopicPreviewPanel from './preview/TopicPreviewPanel';
import language from '../../variables/language';
import '../../css/courses.css';
import '../../css/course-preview.css';
import '../../css/container-layout.css';
import '../../css/media-uploader.css';

export default function Workspace() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const lang = language[language.lang];
   // Arriving here from somewhere other than the plain courses list (e.g.
   // a student's own "Курси" tab) carries a returnTo - same convention as
   // exercise-templates/Builder.jsx's own back button - falling back to
   // the list for the normal /courses entry point.
   const returnTo = location.state && location.state.returnTo ? location.state.returnTo : '/courses';
   const { course, loading, saveCourse, selected, actions } = useCourseWorkspace(id);
   // Which right-panel mode is active - the tree (left) stays mounted and
   // navigable in both, only the right panel swaps.
   const [tab, setTab] = usePersistedState('courses:tab:' + id, 'constructor');
   // Which topic is currently scrolled into view in the Preview panel - a
   // plain (non-persisted) piece of state owned here since it needs to flow
   // from TopicPreviewPanel (reports it via IntersectionObserver) sideways
   // into OutlineTree/PreviewNavList (renders the "you are here" marker) -
   // the two are siblings, this is the shared parent.
   const [activeTopicId, setActiveTopicId] = useState(null);
   // Only reachable via the header button below, never persisted - always
   // starts closed on a fresh page load, and forcing the Constructor tab
   // when opened guarantees the form actually appears where it's supposed
   // to (its own text explicitly promises "in the constructor").
   const [showCourseEditForm, setShowCourseEditForm] = useState(false);
   const toggleCourseEditForm = () => {
      setTab('constructor');
      setShowCourseEditForm(prev => !prev);
   };

   return (
      <Layout>
         {/* Header lives outside <main> (same pattern as every list page's
             PageHeader) so it never scrolls away with the content - only
             .course-workspace-tree-col/.course-workspace-detail-col below
             get their own independent scroll. */}
         <PageHeader name={
            <div className='course-workspace-header-inner'>
               <div className='course-workspace-back-btn' onClick={() => navigate(returnTo)} title='До курсів'>
                  <IconGoBack />
               </div>
               <span className='course-workspace-title'>{course ? course.name : 'Курс'}</span>
               {course ? (
                  <div className={'course-workspace-edit-btn' + (showCourseEditForm ? ' active' : '')}
                     onClick={toggleCourseEditForm} title='Редагувати курс'>
                     <IconEdit style={{ width: 15, height: 15, margin: 0 }} />
                  </div>
               ) : ''}
            </div>
         }>
            <div className='course-workspace-tabs'>
               <div className={'course-workspace-tab' + (tab === 'constructor' ? ' active' : '')} onClick={() => setTab('constructor')}>Конструктор</div>
               <div className={'course-workspace-tab' + (tab === 'preview' ? ' active' : '')} onClick={() => setTab('preview')}>Перегляд</div>
            </div>
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c course-workspace-page'>
               {loading ? (
                  <div className='event-edit-loading'>{lang['Loading ...']}</div>
               ) : !course ? (
                  <div className='event-edit-loading'>Курс не знайдено</div>
               ) : (
                  <>
                     {tab === 'constructor' && showCourseEditForm ? (
                        <CourseEditForm course={course} onSave={saveCourse} onClose={() => setShowCourseEditForm(false)} />
                     ) : ''}
                     <div className='course-workspace-body'>
                        <div className='course-workspace-tree-col'>
                           <OutlineTree courseId={course.id} courseName={course.name} actions={actions}
                              preview={tab === 'preview'} activeTopicId={activeTopicId} />
                        </div>
                        <div className='course-workspace-detail-col'>
                           {tab === 'preview' ? (
                              <TopicPreviewPanel courseId={course.id} selected={selected} actions={actions} onActiveTopicChange={setActiveTopicId} />
                           ) : (
                              <DetailPanel course={course} saveCourse={saveCourse} selected={selected} actions={actions} />
                           )}
                        </div>
                     </div>
                  </>
               )}
            </div>
         </main>
      </Layout>
   );
}
